/**
 * Tiny in-memory cache for "reference data" — courses, instructors,
 * training centers, declarations. These rarely change during a session
 * but were being re-fetched every time the user opened a class.
 *
 * - In-memory only (no localStorage) so a hard refresh always gets fresh data
 * - 60s TTL just in case admin edits something while the form is open
 * - `invalidate()` is exposed for places where we know the data is stale
 *   (e.g. after creating a new training center inside ClassForm)
 */
import axios from 'axios';
import { API_URL } from './utils';

const TTL_MS = 60_000;
const cache = new Map(); // key -> { at: number, value: any }

function get(key) {
  const hit = cache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > TTL_MS) {
    cache.delete(key);
    return null;
  }
  return hit.value;
}

function set(key, value) {
  cache.set(key, { at: Date.now(), value });
}

export function invalidate(key) {
  if (key) cache.delete(key);
  else cache.clear();
}

async function fetchOrCache(key, url) {
  const cached = get(key);
  if (cached) return cached;
  const res = await axios.get(`${API_URL}${url}`);
  const value = res.data;
  set(key, value);
  return value;
}

export const referenceCache = {
  courses: () => fetchOrCache('courses', '/courses'),
  instructors: () => fetchOrCache('instructors', '/instructors'),
  trainingCenters: () => fetchOrCache('training-centers', '/training-centers'),
  declarations: () => fetchOrCache('declarations', '/declarations'),
  invalidate,
};

export default referenceCache;
